import { Fragment } from 'react';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { theme } from '@theguarantors/tg-package-material-ui';

import type {
  OptionBoxOptionsSource,
  ScreeningPresentation,
  SectionConfig,
} from '@/features/LeaseDetails';
import { SectionHeading } from '@/features/LeaseDetails';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';

import { InviteLeaseDetailsSegmentEvents } from './constants';
import {
  approvalStatusValidate,
  monthsOfCoverageValidate,
  securityDepositReplacementAmountValidate,
} from './invite-lease-details.field-schemas';
import { INVITE_LEASE_DETAILS_VALIDATION_MESSAGES as MSG } from './constants';
import type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';
import {
  InviteDepositCoverageWarning,
  InvitePmiCoverageHelper,
  InviteScreeningIntro,
} from './invite-lease-details.screening-chrome';
import {
  institutionalScreeningOptionsSource,
  smbMonthsOptionsSource,
  smbStaticScreeningOptionsSource,
} from './invite-lease-details.screening-options';

type AddressLike = { property_id?: string; address_id?: string; monolith_building_id?: number };

const exampleBlock = (text: string) => (
  <Box component="span" sx={{ display: 'block', mt: 2 }}>
    {text}
  </Box>
);

const monthsHelper = (
  <Fragment>
    Please enter the number of months you&apos;d like covered excluding prepaid rent, concessions
    and security deposit.
    {exampleBlock(
      'Example: If the tenant has a 12 month lease with 1 month free, and the 1st month paid at lease signing, enter "10" for full coverage',
    )}
  </Fragment>
);

const sdrHelper = (selfEnrolledSDRLimit?: number) => (
  <Fragment>
    If you are allowing your renter to replace a cash deposit, please enter the dollar amount.
    {exampleBlock(
      'Example: If you want to collect the equivalent of 1 month security deposit, enter $2,500 for a monthly rent of $2,500.',
    )}
    {selfEnrolledSDRLimit
      ? exampleBlock(`Value cannot exceed the equivalent of ${selfEnrolledSDRLimit} month's rent.`)
      : null}
  </Fragment>
);

// Label dims to grey when the switch is off — matches the legacy form. Reads
// the field value via `useWatch` so it re-renders on toggle.
function DepositSwitchLabel() {
  const checked = Boolean(useWatch({ name: 'includeDepositCoverage' }));
  return (
    <Typography
      sx={{
        color: `${checked ? theme.palette.midnightGrey[950] : '#888888'} !important`,
        fontSize: '16px',
        [theme.breakpoints.down('md')]: { fontSize: '14px', lineHeight: '22px' },
      }}
    >
      For an additional fee, add optional coverage to replace your cash security deposit.
    </Typography>
  );
}

const depositSwitchLabel = <DepositSwitchLabel />;

const rentCoverageOptions: OptionBoxOptionsSource = {
  kind: 'static',
  options: [
    {
      id: 'rent-coverage-company-default',
      value: RentCoverageTypes.CompanyDefault,
      label: "My company's default",
    },
    {
      id: 'rent-coverage-customize',
      value: RentCoverageTypes.Customize,
      label: 'Customize Rent Coverage',
    },
  ],
};

const securityDepositOptions: OptionBoxOptionsSource = {
  kind: 'static',
  options: [
    {
      id: 'security-deposit-coverage-company-default',
      value: SecurityDepositTypes.CompanyDefault,
      label: "My company's default",
    },
    {
      id: 'security-deposit-coverage-customize-or-remove',
      value: SecurityDepositTypes.CustomizeOrRemove,
      label: 'Customize or remove Deposit Coverage',
    },
  ],
};

const CoverageSelectionIntro = (
  <SectionHeading
    title="Coverage selection"
    subtitle="You may set coverage requirements that apply to this deal only, rather than relying on your company's default settings."
  />
);

const PmiCoverageIntro = (
  <SectionHeading
    title="Customize your coverage selection"
    subtitle="You may select exactly how many months of Rent Coverage and Deposit Coverage you'd like."
  />
);

export const buildInviteScreeningSection = (
  deps: InviteLeaseDetailsPresetDeps,
): SectionConfig & ScreeningPresentation => {
  const { isSmbInvite, isPmi, trackInvite, onMilestoneReached, hasCustomScreeningStatusLabels } =
    deps;

  const screeningLabelEvent = {
    onChange: ({
      value,
      optionId,
      getValues,
    }: {
      value: string;
      optionId?: string;
      getValues: (name?: string) => unknown;
    }) => {
      onMilestoneReached?.('hadLabelSelected');
      const address = getValues('address') as AddressLike | undefined;
      trackInvite(InviteLeaseDetailsSegmentEvents.ScreeningLabelSelected, {
        property_id: address?.property_id,
        address_id: address?.address_id,
        has_custom_screening_status_labels: isSmbInvite
          ? false
          : hasCustomScreeningStatusLabels(address?.property_id),
        screening_label_id: optionId,
        mapped_outcome: value,
      });
    },
  };

  if (!isSmbInvite) {
    return {
      visible: true,
      variant: 'institutional',
      title: '',
      slots: {
        beforeFields: <InviteScreeningIntro isSmbInvite={false} />,
      },
      fields: {
        screeningStatus: {
          kind: 'option-box',
          visible: true,
          required: true,
          requiredMessage: MSG.SCREENING_RESULT_REQUIRED,
          defaultValue: '',
          options: institutionalScreeningOptionsSource(),
          layout: 'stacked',
          useDrawerStyles: true,
          validate: approvalStatusValidate(false),
          events: screeningLabelEvent,
        },
      },
    };
  }

  if (isPmi) {
    return {
      visible: true,
      variant: 'smb',
      title: '',
      slots: {
        beforeFields: <InviteScreeningIntro isSmbInvite={true} />,
        afterFields: <InvitePmiCoverageHelper />,
      },
      fields: {
        screeningStatus: {
          kind: 'option-box',
          visible: true,
          required: { not: { whenField: 'customCoverage', equals: true } },
          requiredMessage: MSG.SCREENING_RESULT_REQUIRED,
          defaultValue: '',
          options: smbStaticScreeningOptionsSource(),
          layout: 'stacked',
          useDrawerStyles: true,
          disabled: { whenField: 'customCoverage', equals: true },
          validate: approvalStatusValidate(false),
          events: screeningLabelEvent,
        },
        customCoverage: {
          kind: 'checkbox',
          visible: true,
          defaultValue: false,
          label: 'I want to customize my own coverage',
          events: {
            onChange: ({ value, getValues }) => {
              if (value !== true) {
                return;
              }
              const address = getValues('address') as AddressLike | undefined;
              trackInvite(InviteLeaseDetailsSegmentEvents.RentCoverageSelected, {
                property_id: address?.property_id,
                address_id: address?.address_id,
                value: RentCoverageTypes.Customize,
              });
              trackInvite(InviteLeaseDetailsSegmentEvents.DepositCoverageSelected, {
                property_id: address?.property_id,
                address_id: address?.address_id,
                value: SecurityDepositTypes.CustomizeOrRemove,
              });
            },
          },
        },
      },
    };
  }

  // SMB, non-PMI (always new coverages): DDS-priced months radio + deposit switch.
  // `screeningStatus` is hidden and set by the auto-select effect.
  return {
    visible: true,
    variant: 'smb',
    title: '',
    slots: {
      beforeFields: <InviteScreeningIntro isSmbInvite={true} showStateCoverageInfoBanner={true} />,
      afterFields: <InviteDepositCoverageWarning />,
    },
    fields: {
      leaseGuaranteeMonthsOverride: {
        kind: 'option-box',
        visible: true,
        required: true,
        requiredMessage: MSG.MONTHS_OF_COVERAGE_REQUIRED,
        options: smbMonthsOptionsSource(),
        companionIdField: 'selectedCoverageRuleId',
        useDrawerStyles: true,
        validate: monthsOfCoverageValidate,
        events: screeningLabelEvent,
      },
      includeDepositCoverage: {
        kind: 'switch',
        visible: true,
        defaultValue: false,
        label: depositSwitchLabel,
      },
      // Hidden field set by the SMB auto-select effect; effect only writes
      // values from the allowed set, so the schema validator is dead defense.
      // Visibility-gated validation would skip it anyway (engine behavior).
      screeningStatus: {
        kind: 'text',
        visible: false,
        defaultValue: '',
      },
      selectedCoverageRuleId: { kind: 'text', visible: false },
    },
  };
};

export const buildInviteCoverageSection = (deps: InviteLeaseDetailsPresetDeps): SectionConfig => {
  const { isSmbInvite, isPmi, customCoverageDisabled, selfEnrolledSDRLimit, trackInvite } = deps;

  if (isPmi) {
    // Self-enrolled customize layout, shown only once the customize box is ticked.
    return {
      visible: { whenField: 'customCoverage', equals: true },
      title: '',
      slots: {
        beforeFields: PmiCoverageIntro,
      },
      fields: {
        leaseGuaranteeMonthsOverride: {
          kind: 'text',
          inputType: 'number',
          visible: true,
          required: true,
          requiredMessage: MSG.MONTHS_OF_COVERAGE_REQUIRED,
          label: 'Months of coverage',
          helperText: monthsHelper,
          validate: monthsOfCoverageValidate,
        },
        securityDepositReplacementAmountOverride: {
          kind: 'money',
          visible: true,
          required: true,
          requiredMessage: MSG.SECURITY_DEPOSIT_REPLACEMENT_REQUIRED,
          label: 'Security deposit replacement amount',
          helperText: sdrHelper(selfEnrolledSDRLimit),
          validate: securityDepositReplacementAmountValidate(selfEnrolledSDRLimit),
        },
      },
    };
  }

  if (isSmbInvite) {
    // SMB non-PMI has no coverage section (deposit handled by the screening switch).
    return { visible: false, fields: {} };
  }

  // Institutional: when the user lacks customize permission the section is hidden
  // and a requirements banner is shown form-level (via the preset's ui.slots.top).
  if (customCoverageDisabled) {
    return { visible: false, fields: {} };
  }

  return {
    visible: true,
    title: '',
    slots: {
      beforeFields: CoverageSelectionIntro,
    },
    fields: {
      rentCoverage: {
        kind: 'option-box',
        visible: true,
        required: true,
        requiredMessage: MSG.RENT_COVERAGE_REQUIRED,
        defaultValue: RentCoverageTypes.CompanyDefault,
        label: 'Rent Coverage',
        options: rentCoverageOptions,
        useDrawerStyles: true,
        events: {
          onChange: ({ value, getValues }) => {
            if (value !== RentCoverageTypes.Customize) {
              return;
            }
            const address = getValues('address') as AddressLike | undefined;
            trackInvite(InviteLeaseDetailsSegmentEvents.RentCoverageSelected, {
              property_id: address?.property_id,
              address_id: address?.address_id,
              value,
            });
          },
        },
      },
      leaseGuaranteeMonthsOverride: {
        kind: 'text',
        inputType: 'number',
        visible: { whenField: 'rentCoverage', equals: RentCoverageTypes.Customize },
        required: { whenField: 'rentCoverage', equals: RentCoverageTypes.Customize },
        requiredMessage: MSG.MONTHS_OF_COVERAGE_REQUIRED,
        fade: true,
        label: 'Months of coverage',
        helperText: monthsHelper,
        errorHelperText: monthsHelper,
        validate: monthsOfCoverageValidate,
      },
      securityDepositCoverage: {
        kind: 'option-box',
        visible: true,
        required: true,
        requiredMessage: MSG.SECURITY_DEPOSIT_COVERAGE_REQUIRED,
        defaultValue: SecurityDepositTypes.CompanyDefault,
        label: 'Security Deposit Coverage',
        options: securityDepositOptions,
        useDrawerStyles: true,
        events: {
          onChange: ({ value, getValues }) => {
            if (value !== SecurityDepositTypes.CustomizeOrRemove) {
              return;
            }
            const address = getValues('address') as AddressLike | undefined;
            trackInvite(InviteLeaseDetailsSegmentEvents.DepositCoverageSelected, {
              property_id: address?.property_id,
              address_id: address?.address_id,
              value,
            });
          },
        },
      },
      securityDepositReplacementAmountOverride: {
        kind: 'money',
        visible: {
          whenField: 'securityDepositCoverage',
          equals: SecurityDepositTypes.CustomizeOrRemove,
        },
        required: {
          whenField: 'securityDepositCoverage',
          equals: SecurityDepositTypes.CustomizeOrRemove,
        },
        requiredMessage: MSG.SECURITY_DEPOSIT_REPLACEMENT_REQUIRED,
        fade: true,
        label: 'Security deposit amount',
        helperText: sdrHelper(selfEnrolledSDRLimit),
        errorHelperText: sdrHelper(selfEnrolledSDRLimit),
        validate: securityDepositReplacementAmountValidate(selfEnrolledSDRLimit),
      },
    },
  };
};
